<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/dialog';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {ActionButton} from '@react-spectrum/button';
import {AlertDialog, DialogTrigger} from '@react-spectrum/dialog';
```

---
category: Overlays
---

# AlertDialog

<p>{docs.exports.AlertDialog.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['AlertDialog']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/dialog/#Options'}
  ]} />

## Example

```tsx example
<DialogTrigger>
  <ActionButton>Save</ActionButton>
  <AlertDialog title="Low Disk Space" variant="warning" primaryActionLabel="Confirm">
    You are running low on disk space. Delete unnecessary files to free up space.
  </AlertDialog>
</DialogTrigger>
```

## Content

Unlike Dialog, the layout in AlertDialog is very deliberate. The body of an AlertDialog can be provided by passing 
children to the component. The AlertDialog also supports having up to three buttons in its footer: a primary button,
a secondary button, and a cancel button. Each button can be rendered by providing a string to the `primaryActionLabel`, `secondaryActionLabel`,
and `cancelLabel` respectively. Be sure to localize any strings provided to the AlertDialog and to the button labels as well. 

```tsx example
<DialogTrigger>
  <ActionButton>Exit</ActionButton>
  <AlertDialog variant="information" title="Register profile" primaryActionLabel="Register" secondaryActionLabel="Remind me later" cancelLabel="Cancel">
    You have not saved your profile information for this account. Would you like to register now?
  </AlertDialog>
</DialogTrigger>
```

### Accessibility

If any of the buttons in the ActionDialog's footer should be focused on render, specify which one via the `autoFocusButton` prop.

```tsx example
<DialogTrigger>
  <ActionButton>Save</ActionButton>
  <AlertDialog variant="confirmation" title="Save file" primaryActionLabel="Save" cancelLabel="Cancel" autoFocusButton="primary">
    A file with the same name already exists. Overwrite?
  </AlertDialog>
</DialogTrigger>
```

## Labeling

ActionDialogs should always have a visible title, specified by passing a string as the `title` prop. This string should be localized
so that the ActionDialog is properly internationalized.

## Events

AlertDialog accepts an `onPrimaryAction`, `onSecondaryAction` and `onCancel` prop, triggered when the AlertDialog's confirm or cancel buttons are clicked respectively.

The example below uses `onPrimaryAction`, `onSecondaryAction` and `onCancel` to tell the user which button was clicked to close the AlertDialog.

```tsx example
function Example() {
  let onPrimaryAction = () => alert('Primary button clicked.');
  let onSecondaryAction = () => alert('Secondary button clicked.');
  let alertCancel = () => alert('Cancel button clicked.');

  return (
    <DialogTrigger>
      <ActionButton>
        Publish
      </ActionButton>
      <AlertDialog
        variant="confirmation" 
        title="Confirm Publish" 
        primaryActionLabel="Publish" 
        secondaryActionLabel="Save as draft" 
        cancelLabel="Cancel"
        onCancel={alertCancel}
        onPrimaryAction={onPrimaryAction}
        onSecondaryAction={onSecondaryAction}>
        Are you sure you want to publish this document?
      </AlertDialog>
    </DialogTrigger>
  );
}
```

## Props

<PropTable component={docs.exports.AlertDialog} links={docs.links} />

## Visual Options

### Variant
[View guidelines](https://spectrum.adobe.com/page/dialog/#Options)

```tsx example
<DialogTrigger marginEnd="20px">
  <ActionButton>Exit</ActionButton>
  <AlertDialog variant="confirmation" title="Exit instance?" primaryActionLabel="Confirm" cancelLabel="Cancel">
    Exit dungeon instance and return to main hub?
  </AlertDialog>
</DialogTrigger>

<DialogTrigger marginEnd="20px">
  <ActionButton>New file</ActionButton>
  <AlertDialog variant="information" title="Connect your account" primaryActionLabel="Continue" cancelLabel="Cancel">
    Please connect an existing account to sync any new files.
  </AlertDialog>
</DialogTrigger>

<DialogTrigger marginEnd="20px">
  <ActionButton>Delete</ActionButton>
  <AlertDialog variant="destructive" title="Delete file" primaryActionLabel="Delete" cancelLabel="Cancel">
    This will permanently delete the selected file. Continue?
  </AlertDialog>
</DialogTrigger>

<DialogTrigger marginEnd="20px">
  <ActionButton>Login</ActionButton>
  <AlertDialog variant="error" title="Unable to connect" primaryActionLabel="Retry" cancelLabel="Cancel">
    Something went wrong while connecting to the server. Please try again in a couple minutes.
  </AlertDialog>
</DialogTrigger>

<DialogTrigger>
  <ActionButton>Enter</ActionButton>
  <AlertDialog variant="warning" title="Raid instance" primaryActionLabel="Confirm" cancelLabel="Cancel">
    The following encounter meant for parties of 4 or more. Enter anyways?
  </AlertDialog>
</DialogTrigger>
```

### isPrimaryActionDisabled

Disables the primary button.

```tsx example
<DialogTrigger>
  <ActionButton>Upgrade</ActionButton>
  <AlertDialog isPrimaryActionDisabled variant="confirmation" title="Upgrade subscription" primaryActionLabel="Upgrade" cancelLabel="Cancel">
    Upgrade subscription for an additional $14.99 a month?
  </AlertDialog>
</DialogTrigger>
```

### isSecondaryActionDisabled

Disables the secondary button.

```tsx example
<DialogTrigger>
  <ActionButton>Upgrade</ActionButton>
  <AlertDialog isSecondaryActionDisabled variant="confirmation" title="Upgrade subscription" primaryActionLabel="Upgrade" secondaryActionLabel="Apply Coupon" cancelLabel="Cancel">
    Upgrade subscription for an additional $14.99 a month?
  </AlertDialog>
</DialogTrigger>
```
